<template>
  <div>
    <v-card v-if="!tfaEnabled" width="400" class="mx-auto mt-5">
      <v-card-title class="pb-2">
        <h1>Login</h1>
      </v-card-title>
      <v-card-text>
        <div class="mb-3">
          New to Ferro?
          <a href="https://www.projectferro.com/contact/">Contact us today</a>
          .
        </div>
        <v-form>
          <v-text-field
            id="username"
            v-model="username"
            label="Email Address"
            prepend-icon="mdi-account-circle"
            v-on:keyup.enter="login(username, password)"
          />
          <v-text-field
            id="password"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            label="Password"
            prepend-icon="mdi-lock"
            v-on:keyup.enter="login(username, password)"
          >
            <template v-slot:append>
              <v-btn icon @click="showPassword = !showPassword" tabindex="-1">
                <v-icon>{{ showPassword ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </v-form>
        <p v-if="showErrorMessageOnForm" class="error">
          {{ errorMessageOnForm }}
        </p>
        <v-row class="pt-3">
          <v-col class="pt-0">
            <v-checkbox v-model="rememberUsername" label="Remember me" color="primary" dense hide-details />
          </v-col>
          <v-col class="text-right pt-2">
            Forgot your
            <router-link :to="forgotPassword">password?</router-link>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          id="loginButton"
          color="primary"
          :disabled="disableLogin"
          @click="login(username, password)"
          v-on:keyup.enter="login(username, password)"
        >
          Login
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else width="400" class="mx-auto mt-5">
      <v-card-title class="pb-2">
        <h1>Verify</h1>
      </v-card-title>
      <v-card-text>
        <div class="mb-3">Please enter the 6 digit code we just sent you</div>
        <v-form>
          <v-text-field
            autofocus
            v-model="tfaCode"
            label="Verification Code"
            type="number"
            :rules="[codeRule]"
            v-on:keydown.enter.prevent="validateCode"
          />
        </v-form>
        <p v-if="errorMessageOnForm !== undefined" class="error">
          {{ errorMessageOnForm }}
        </p>
        <div v-if="!codeVerifyHelp" class="text-decoration-underline" @click="codeVerifyHelp = true">
          <v-btn class="pl-0" text x-small color="primary">I didn't receive a code</v-btn>
        </div>
        <div v-else>
          Please try to request again after a moment. If the problem persists, please contact support.<br />
          <v-btn class="mt-4" :disabled="!requestCodeAgainButton" outlined color="primary" @click="requestVerifyCodeAgain"
            >Request Another Code</v-btn
          >
        </div>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <v-btn type="form" text color="primary" @click.prevent="backToLogin">Back To Login </v-btn>
        <v-spacer />
        <v-btn
          type="form"
          id="validate"
          color="primary"
          @click.prevent="validateCode"
          v-on:keyup.enter="validateCode"
          :disabled="!tfaCodeValid"
          >Validate Code
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { ACCOUNT_SELECTOR, ADMIN_ACCOUNTS, BROKER_ADMIN_SELECT, DASHBOARD, FORGOT_PASSWORD, MANAGE_MY_USER } from '@/router/route-names'
import { getAuthenticatedUser, sendTfaVerification, verifyTfaCode } from '@/components/shared/security/auth-utils'

export default {
  name: 'LoginForm',

  data() {
    return {
      tfaEnabled: false,
      tfaCode: undefined,
      user: {},
      showErrorMessageOnForm: false,
      errorMessageOnForm: undefined,
      username: undefined,
      password: undefined,
      showPassword: false,
      rememberUsername: undefined,
      codeRule: v => {
        if (v === undefined || (!isNaN(parseInt(v)) && v.length === 6)) return true
        return 'Code has to be 6 digits'
      },
      codeVerifyHelp: false,
      requestCodeAgainButton: true,
    }
  },
  computed: {
    ...mapGetters({
      getActiveAccount: 'getActiveAccount',
      getAuthedAccounts: 'getAuthedAccounts',
      isAnAdmin: 'isAnAdmin',
      isBrokerageAdmin: 'isBrokerageAdmin',
      isBrokerageUser: 'isBrokerageUser',
      getUser: 'getUser',
      redirectUrl: 'getRedirectUrl',
    }),
    disableLogin() {
      if (this.username === null || this.username === undefined || this.username.length === 0) return true
      if (this.password === null || this.password === undefined || this.password.length === 0) return true
      return false
    },
    loginParams() {
      return {
        passedUsername: this.username,
        passedPassword: this.password,
      }
    },
    loginObject() {
      return {
        name: 'Register',
        params: this.loginParams,
      }
    },
    forgotPassword() {
      return {
        name: FORGOT_PASSWORD,
      }
    },
    tfaCodeValid() {
      return this.tfaCode !== undefined && this.tfaCode.length === 6
    },
  },
  mounted() {
    this.username = localStorage.getItem('previously-logged-in-user')
    this.rememberUsername = localStorage.getItem('remember-me-checked') !== 'false'
  },
  methods: {
    ...mapActions({
      setUser: 'setUser',
    }),
    goToRegister() {
      // must match props on Register
      this.$router.push(this.loginObject)
    },
    resetErrorMessage() {
      this.showErrorMessageOnForm = false
      this.errorMessageOnForm = undefined
    },

    async login(username, password) {
      if (this.disableLogin) {
        return
      }
      this.resetErrorMessage()

      try {
        const { data } = await this.$http.post(`/auth/login`, { username, password })
        this.user = data

        this.setLocalStorageItems(username)

        if (data.tfaEnabled) {
          // activate two-factor authentication
          this.tfaEnabled = true
          await this.activateTfa(this.user.tfaPhone, this.user.tfaType.toUpperCase())
        } else {
          // or get user info and route to the correct location
          await this.setAuthenticatedUser()
          this.routeLogic()
        }
      } catch (error) {
        this.handleError(error)
      }
    },

    async activateTfa(tfaPhone, tfaType) {
      this.errorMessageOnForm = undefined
      const response = await sendTfaVerification(tfaPhone, tfaType)
      if (response.status !== 200) this.errorMessageOnForm = response['message']
    },

    async validateCode() {
      this.errorMessageOnForm = undefined
      if (this.tfaCodeValid) {
        const response = await verifyTfaCode(this.tfaCode, this.user.tfaPhone)
        if (response.status === 200 && response.data === 'approved') {
          await this.setAuthenticatedUser()
          this.routeLogic()
        } else {
          this.errorMessageOnForm = response['message'] || 'Unable to confirm the code. Please double check and try again.'
        }
      }
    },

    requestVerifyCodeAgain() {
      this.codeVerifyHelp = false
      this.errorMessageOnForm = undefined
      this.requestCodeAgainButton = false
      this.activateTfa(this.user.tfaPhone, this.user.tfaType.toUpperCase())
      this.$alert('Code requested')
      setTimeout(() => (this.requestCodeAgainButton = true), 30000)
    },

    async setAuthenticatedUser() {
      try {
        await getAuthenticatedUser()
      } catch (error) {
        this.handleError(error)
      }
    },

    setLocalStorageItems(username) {
      if (this.rememberUsername) {
        localStorage.setItem('remember-me-checked', true)
        localStorage.setItem('previously-logged-in-user', username)
      } else {
        localStorage.setItem('remember-me-checked', false)
        localStorage.removeItem('previously-logged-in-user')
      }
    },

    routeLogic() {
      if (this.redirectUrl) {
        this.$router.push(this.redirectUrl)
      } else {
        if (this.isAnAdmin) this.goTo(ADMIN_ACCOUNTS)
        else if (this.getUser.hasMultipleAccounts) this.goTo(ACCOUNT_SELECTOR)
        else if (this.getActiveAccount.accountCode) this.goToDashboard(this.getActiveAccount.accountCode)
        else if (this.isBrokerageUser) this.goTo(BROKER_ADMIN_SELECT)
        else this.goTo(MANAGE_MY_USER)
      }
    },

    goToDashboard(accountId) {
      // must match props on Register
      this.$router
        .push({
          name: DASHBOARD,
          params: { accountId: accountId },
        })
        .catch(e => {
          console.log('failed to route')
          console.log(e)
        })
    },

    goTo(page) {
      this.$router
        .push({
          name: page,
        })
        // eslint-disable-next-line no-unused-vars
        .catch(e => {})
    },

    backToLogin() {
      window.location.reload()
    },

    handleError(error) {
      if (error && error.response && error.response.status === 422) {
        this.errorMessageOnForm = 'Unknown username/password'
        this.showErrorMessageOnForm = true
        // invalid credentials
        return
      }
      console.error('Error loading your account.')

      if (error && error.message === 'Network Error') {
        this.$error('There has been an issue communicating with Ferro. Please give us a few minutes and try again.')
        return
      }
      this.$error('An unexpected error occurred. Please try again in a few moments or contact Ferro if it continues.')
    },
  },
}
</script>
<style lang="scss" scoped>
@import '~@/scss/variables.scss'; // Using this should get you the variables

h1 {
  color: $primary;
}

::v-deep .v-label {
  font-size: 14px;
}

.error {
  padding: 2px 0px 2px 10px;
  color: white;
}
</style>
